import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import FlowBuilderModal from "pages/FlowBuilderv2/Elements/FlowBuilderModal";
import {
  flowLeadStatsMergeFieldsQuery,
  flowPlaceholdersQuery,
} from "pages/FlowBuilderv2/flowBuilderQueries";
import { MessageNodeData } from "pages/FlowBuilderv2/Nodes/NodeData";
import EmailPreview from "pages/TemplateTablev2/Modals/previews/EmailPreview";
import {
  EMAIL_DEFAULTS,
  EmailTemplateDefault,
} from "pages/TemplateTablev2/defaults/email";
import {
  composeEmail,
  isLetterCompatible,
} from "pages/TemplateTablev2/defaults/email-compose";
import { EmailContentPreset } from "pages/TemplateTablev2/defaults/email-content";
import AiPromptEditor, { AiPromptEditorHandle } from "./AiPromptEditor";
import { startPlaceholderDrag } from "./placeholderDrag";
import {
  PLACEHOLDER_FIELDS,
  PlaceholdersResponse,
  buildPlaceholderValues,
} from "./placeholderFields";
import { LeadStatsMergeField } from "types/LeadStatsMergeField";

type EmailOverride = NonNullable<MessageNodeData["emailOverride"]>;

const DEFAULT_STYLE_KEY = "design.plain-letter";

// The styles a body of text can be composed onto. Non-letter designs (receipt,
// newsletter, etc.) are single-piece and only editable in the full builder.
const LETTER_STYLES: EmailTemplateDefault[] = EMAIL_DEFAULTS.filter((d) =>
  isLetterCompatible(d.key)
);

// Pull readable paragraph text out of a template's composed HTML to seed the
// editor the first time someone edits an email that has never been touched here.
const htmlToBodyText = (html?: string): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const ps = tmp.querySelectorAll("p");
  if (ps.length)
    return Array.from(ps)
      .map((p) => (p.textContent || "").trim())
      .filter(Boolean)
      .join("\n");
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

// Turn the edited body text into a content preset the composer understands:
// each non-empty line becomes a paragraph. No auto signature — what the user
// types is exactly what sends.
const bodyToContent = (body: string, subject: string): EmailContentPreset => ({
  key: "node.inline",
  category: "inline",
  name: "inline",
  description: "",
  subject,
  paragraphs: body
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean),
  includeSignature: false,
});

const composeBody = (body: string, styleKey: string, subject: string) => {
  const style =
    LETTER_STYLES.find((s) => s.key === styleKey) ||
    LETTER_STYLES.find((s) => s.key === DEFAULT_STYLE_KEY) ||
    LETTER_STYLES[0];
  return composeEmail(style, bodyToContent(body, subject));
};

interface EmailContentEditorProps {
  templateSubject?: string;
  // The template's composed HTML, used to seed the editor and to preview when
  // the node hasn't overridden anything yet.
  templateText?: string;
  override?: MessageNodeData["emailOverride"];
  onChange: (next: EmailOverride) => void;
  // The preview dialog is opened from the side panel footer, so its open state
  // is owned by the parent and passed in here.
  previewOpen?: boolean;
  onPreviewClose?: () => void;
}

const EmailContentEditor: FC<EmailContentEditorProps> = ({
  templateSubject,
  templateText,
  override,
  onChange,
  previewOpen,
  onPreviewClose,
}) => {
  const queryClient = useQueryClient();
  const subject = templateSubject || "";

  // Saved placeholder values, for the dropdown and the chips in the editor.
  const [placeholders, setPlaceholders] = useState<PlaceholdersResponse>({});
  const [leadStatsFields, setLeadStatsFields] = useState<LeadStatsMergeField[]>(
    []
  );
  useEffect(() => {
    (async () => {
      try {
        const data = (await queryClient.ensureQueryData(
          flowPlaceholdersQuery()
        )) as PlaceholdersResponse;
        setPlaceholders(data || {});
      } catch {
        setPlaceholders({});
      }
    })();
  }, [queryClient]);
  useEffect(() => {
    (async () => {
      try {
        const data = await queryClient.ensureQueryData(
          flowLeadStatsMergeFieldsQuery("email")
        );
        setLeadStatsFields(data || []);
      } catch {
        setLeadStatsFields([]);
      }
    })();
  }, [queryClient]);
  const placeholderValues = useMemo(
    () => buildPlaceholderValues(placeholders),
    [placeholders]
  );
  const leadStatsPlaceholderValues = useMemo(
    () =>
      Object.fromEntries(
        leadStatsFields
          .filter((field) => !field.disabled)
          .map((field) => [
            field.token,
            field.exampleValue || field.displayName || field.token,
          ])
      ),
    [leadStatsFields]
  );
  const editorPlaceholderValues = useMemo(
    () => ({ ...placeholderValues, ...leadStatsPlaceholderValues }),
    [placeholderValues, leadStatsPlaceholderValues]
  );
  const availablePlaceholders = useMemo(
    () => PLACEHOLDER_FIELDS.filter((f) => placeholderValues[f.tag]),
    [placeholderValues]
  );
  const groupedLeadStatsFields = useMemo(() => {
    return leadStatsFields.reduce<Record<string, LeadStatsMergeField[]>>(
      (acc, field) => {
        const group = field.group || "LeadStats";
        acc[group] = [...(acc[group] || []), field];
        return acc;
      },
      {}
    );
  }, [leadStatsFields]);

  // Body + style are seeded once from the node's own copy if it has one,
  // otherwise from the template the node points at.
  const [body, setBody] = useState<string>(
    () => override?.body ?? htmlToBodyText(templateText)
  );
  const [styleKey, setStyleKey] = useState<string>(
    () => override?.styleKey ?? DEFAULT_STYLE_KEY
  );

  // templateText arrives asynchronously (the template list fetches after mount).
  // If body is still empty and no override exists, seed it once when the
  // template data lands.
  useEffect(() => {
    if (!templateText) return;
    if (override?.body) return;
    if (body) return;
    setBody(htmlToBodyText(templateText));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateText]);

  const editorRef = useRef<AiPromptEditorHandle>(null);
  const [placeholderMenuOpen, setPlaceholderMenuOpen] = useState(false);
  const placeholderMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!placeholderMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (
        placeholderMenuRef.current &&
        !placeholderMenuRef.current.contains(e.target as Node)
      )
        setPlaceholderMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [placeholderMenuOpen]);

  // Recompose and push the override up whenever the body or style changes.
  const commit = (nextBody: string, nextStyleKey: string) => {
    const composed = composeBody(nextBody, nextStyleKey, subject);
    onChange({
      body: nextBody,
      styleKey: nextStyleKey,
      html: composed?.text,
      css: composed?.style,
    });
  };

  const handleBodyChange = (next: string) => {
    setBody(next);
    commit(next, styleKey);
  };

  const handleStyleChange = (next: string) => {
    setStyleKey(next);
    commit(body, next);
  };

  // The preview renders the current text composed with the selected style, so
  // it always matches the style dropdown. composeBody returns fully inlined
  // HTML; fall back to the raw template only if composition fails.
  const previewHtml = useMemo(
    () => composeBody(body, styleKey, subject)?.text ?? templateText ?? "",
    [body, styleKey, subject, templateText]
  );

  return (
    <div className="px-5 pb-5 flex flex-col gap-[8px] border-b border-themed-border flex-1 min-h-0">
      <div className="font-semibold">Email</div>

      <div className="flex items-center justify-between gap-2">
        <div className="text-[13px] leading-[20px] min-h-[20px]">
          {subject && (
            <>
              <span className="text-themed-textSecondary">Subject: </span>
              {subject}
            </>
          )}
        </div>
        <div className="relative shrink-0" ref={placeholderMenuRef}>
          <button
            type="button"
            onClick={() => setPlaceholderMenuOpen((o) => !o)}
            className="flex items-center gap-1 px-2 py-1 rounded text-[12px] border border-themed-borderSecondary bg-themed-card text-themed-text hover:border-[#7C3AED] hover:text-[#7C3AED]"
          >
            Placeholders
            <span className="text-[16px] leading-none">▾</span>
          </button>
          {placeholderMenuOpen && (
            <div className="absolute right-0 mt-1 z-10 w-[260px] max-h-[260px] overflow-y-auto bg-themed-card border border-themed-borderSecondary rounded shadow-lg py-1">
              {availablePlaceholders.length === 0 ? (
                <div className="px-3 py-2 text-[12px] text-themed-textSecondary leading-[18px]">
                  You haven&apos;t set up any placeholder values yet. Add them
                  on{" "}
                  <span className="font-medium">
                    Settings &rarr; Placeholders
                  </span>{" "}
                  to use them here.
                </div>
              ) : (
                availablePlaceholders.map((p) => (
                  <button
                    key={p.tag}
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/placeholder", p.tag);
                      e.dataTransfer.effectAllowed = "move";
                      startPlaceholderDrag(e, placeholderValues[p.tag] || "");
                      setTimeout(() => setPlaceholderMenuOpen(false), 0);
                    }}
                    onClick={() => {
                      editorRef.current?.insertPlaceholder(p.tag);
                      setPlaceholderMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-1.5 hover:bg-themed-background flex items-center gap-2 cursor-grab"
                  >
                    <span
                      className="inline-block align-baseline rounded px-1 text-[13px] truncate"
                      style={{
                        color: "var(--foreground)",
                        backgroundColor:
                          "color-mix(in srgb, var(--muted) 50%, transparent)",
                      }}
                    >
                      {placeholderValues[p.tag]}
                    </span>
                  </button>
                ))
              )}
              {Object.entries(groupedLeadStatsFields).map(([group, fields]) => (
                <div
                  key={group}
                  className="border-t border-themed-border mt-1 pt-1"
                >
                  <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-themed-textSecondary">
                    {group}
                  </div>
                  {fields.map((field) => (
                    <button
                      key={field.id}
                      type="button"
                      disabled={field.disabled}
                      draggable={!field.disabled}
                      onDragStart={(e) => {
                        if (field.disabled) return;
                        e.dataTransfer.setData("text/placeholder", field.token);
                        e.dataTransfer.effectAllowed = "move";
                        startPlaceholderDrag(
                          e,
                          field.exampleValue || field.displayName
                        );
                        setTimeout(() => setPlaceholderMenuOpen(false), 0);
                      }}
                      onClick={() => {
                        if (field.disabled) return;
                        editorRef.current?.insertPlaceholder(field.token);
                        setPlaceholderMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 flex flex-col gap-0.5 ${
                        field.disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-themed-background cursor-grab"
                      }`}
                    >
                      <span className="text-[13px] text-themed-text">
                        {field.displayName}
                      </span>
                      <span className="text-[11px] text-themed-textSecondary">
                        {`{{ ${field.token} }}`}
                        {field.disabled && field.disabledReason
                          ? ` - ${field.disabledReason}`
                          : ""}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <AiPromptEditor
        ref={editorRef}
        value={body}
        onChange={handleBodyChange}
        placeholderValues={editorPlaceholderValues}
        placeholder="Type the email message contacts will receive"
        className="flex-1 min-h-0"
      />

      <FlowBuilderModal
        isOpen={!!previewOpen}
        onClose={() => onPreviewClose?.()}
        className="!w-[800px] !max-w-[92vw] !h-[80vh] !p-0 overflow-hidden rounded-lg shadow-2xl"
      >
        <div className="w-full h-full flex flex-col bg-themed-card">
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-themed-border">
            <div className="font-semibold text-[15px]">Email preview</div>
            <select
              value={styleKey}
              onChange={(e) => handleStyleChange(e.target.value)}
              className="px-[10px] py-[5px] rounded-sm border border-themed-border bg-themed-background text-[13px]"
            >
              {LETTER_STYLES.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-5">
            <EmailPreview subject={subject} text={previewHtml} />
          </div>
        </div>
      </FlowBuilderModal>
    </div>
  );
};

export default EmailContentEditor;
